/**
 * Payment Plan builder — Customer File quick action.
 *
 * One panel: frequency (daily/weekly per the agreement) + plan-type toggles
 * + parameters on the left, a LIVE editable proposal table on the right.
 * Email step takes over the whole dialog and can send a NEW email or REPLY
 * inside an existing thread from the case inbox. Export to PDF any time.
 */
import { useEffect, useMemo, useState } from "react";
import { desktopOnly } from "@/components/phone/DesktopOnlyNote";
import { cur, fmtMoney, isAusDesk, rl, ageingBasePath, setCollectionsRegion } from "@/lib/regionFmt";
import { toast } from "sonner";
import { WizardDebugButton } from "@/components/ReportBugDialog";
import {
  Loader2, HandCoins, CalendarClock, PauseCircle, Scale as ScaleIcon,
  TrendingDown, Send, Save, AlertTriangle, Plus, X, RotateCcw, Pencil,
  FileDown, Mail, Reply, ChevronLeft, Layers, Wand2,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  previewPaymentPlan, createPaymentPlan, sendPaymentPlanEmail,
  getCaseContactEmails, getPaymentPlanMeta, exportPaymentPlanPdf,
  getAgeingM365Emails,
} from "@/lib/api";

const GBP = (n) => Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const UK = (iso) => { try { const [y, m, d] = String(iso).split("-"); return `${d}/${m}/${y}`; } catch { return iso; } };

const PLAN_OPTIONS = [
  { id: "reduced_then_blend", icon: TrendingDown, label: "Reduced → blend" },
  { id: "holiday_then_lump",  icon: PauseCircle,  label: "Holiday → catch-up" },
  { id: "temp_review",        icon: CalendarClock, label: "Temp + review" },
  { id: "settlement",         icon: ScaleIcon,    label: "Settlement" },
  { id: "stepped",            icon: Layers,       label: "Stepped plan" },
];
const STEP_MODES = [
  { id: "periods",        label: "for N periods" },
  { id: "until_cleared",  label: "until cleared" },
  { id: "blend_maturity", label: "blend until maturity" },
];
const newPhase = (amount = "") => ({ amount, periods: "", mode: "periods" });

function paramsReady(planType, p, meta) {
  if (planType === "reduced_then_blend")
    return Number(p.pay_pct) > 0 && Number(p.reduced_periods) > 0 &&
      (meta?.remaining_count > 0 || Number(p.blend_periods) > 0);
  if (planType === "holiday_then_lump") return Number(p.holiday_periods) > 0;
  if (planType === "temp_review") return Number(p.temp_amount) > 0 && Number(p.temp_periods) > 0;
  if (planType === "settlement") return !!p.settlement_date;
  if (planType === "stepped") {
    const ph = p.phases || [];
    return ph.length > 0 && ph.every((x, i) => x.amount !== "" && Number(x.amount) >= 0 &&
      ((x.mode || "periods") !== "periods" ? i === ph.length - 1 : Number(x.periods) > 0));
  }
  return false;
}

function PaymentPlanWizardInner({ caseRow, onClose, onDone }) {
  const [meta, setMeta] = useState(null);
  const [metaErr, setMetaErr] = useState(null);
  const [freq, setFreq] = useState(null);               // "daily" | "weekly" — per the agreement
  const [planType, setPlanType] = useState(null);
  const [params, setParams] = useState({});
  const [rows, setRows] = useState([]);
  const [headline, setHeadline] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [reviewInfo, setReviewInfo] = useState(null);   // {date, remaining}
  const [approvalInfo, setApprovalInfo] = useState(null); // {breaches} when plan exceeds MY limits
  const [previewBusy, setPreviewBusy] = useState(false);
  const [planErr, setPlanErr] = useState(null);
  const [custom, setCustom] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [mode, setMode] = useState("build");            // build | email
  const [savedPlan, setSavedPlan] = useState(null);

  // Email state
  const [emailMode, setEmailMode] = useState("new");    // new | reply
  const [contacts, setContacts] = useState([]);
  const [inboxEmails, setInboxEmails] = useState(null); // null = not loaded yet
  const [replyMsg, setReplyMsg] = useState(null);
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [intro, setIntro] = useState("");

  const unit = freq === "daily" ? "days" : freq === "monthly" ? "months" : "weeks";   // Emma, 26 Oct 2026: phases said "weeks" on a monthly plan

  useEffect(() => {
    (async () => {
      try {
        const r = await getPaymentPlanMeta(caseRow.case_id);
        setMeta(r.meta);
        const fd = r.meta?.frequency_days;
        const f = fd === 1 ? "daily" : (fd >= 27 && fd <= 32) ? "monthly" : "weekly";
        setFreq((cur) => cur || f);
        setParams((p) => ({ ...p, frequency: p.frequency || f }));
      } catch (e) {
        setMetaErr(e?.response?.data?.detail || e.message);
      }
    })();
  }, [caseRow.case_id]);

  const pickFreq = (f) => {
    setFreq(f);
    setCustom(false);
    setParams((p) => ({ ...p, frequency: f }));
  };

  // Live regeneration — debounce inputs, skip while hand-editing
  const paramsKey = JSON.stringify(params);
  useEffect(() => {
    if (!meta || !planType || custom) return;
    if (!paramsReady(planType, params, meta)) { setRows([]); setHeadline(null); setWarnings([]); setReviewInfo(null); setApprovalInfo(null); return; }
    const t = setTimeout(async () => {
      try {
        setPreviewBusy(true);
        const r = await previewPaymentPlan(caseRow.case_id, { plan_type: planType, params });
        setRows(r.rows || []);
        setHeadline(r.headline);
        setWarnings(r.warnings || []);
        setReviewInfo(r.review_date ? { date: r.review_date, remaining: r.remaining_after_review } : null);
        setApprovalInfo(r.requires_approval ? { breaches: r.breaches || [] } : null);
        setPlanErr(null);
      } catch (e) {
        setPlanErr(e?.response?.data?.detail || e.message);
        setRows([]);
      } finally {
        setPreviewBusy(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [planType, paramsKey, meta, custom]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mode !== "email") return;
    (async () => {
      try {
        const r = await getCaseContactEmails(caseRow.case_id);
        setContacts(r.contacts || []);
        const primary = (r.contacts || []).find((c) => c.primary) || (r.contacts || [])[0];
        if (primary && !to) setTo(primary.email);
      } catch { /* type manually */ }
    })();
    if (!subject) setSubject(`Payment Plan Proposal — ${caseRow.customer_name || caseRow.loan_application_id}`);
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadInbox = async () => {
    if (inboxEmails !== null) return;
    try {
      setInboxEmails([]);
      const r = await getAgeingM365Emails(caseRow.case_id, { limit: 15 });
      setInboxEmails((r.emails || []).filter((e) => !e.is_read_receipt));
    } catch (e) {
      toast.error("Couldn't load the case inbox: " + (e?.response?.data?.detail || e.message));
      setInboxEmails([]);
    }
  };

  const setP = (k, v) => setParams((p) => ({ ...p, [k]: v }));

  const recompute = (list) => {
    let bal = meta?.outstanding_total || 0;
    return list.map((r) => {
      bal = Math.round((bal - Number(r.amount || 0)) * 100) / 100;
      return { ...r, balance: Math.max(bal, 0) };
    });
  };

  const editRow = (i, patch) => {
    setCustom(true);
    setRows((prev) => recompute(prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r))));
  };
  const removeRow = (i) => {
    setCustom(true);
    setRows((prev) => recompute(prev.filter((_, idx) => idx !== i)));
  };
  const bulkSet = ({ from, count, amount }) => {
    setCustom(true);
    setRows((prev) => recompute(prev.map((r, idx) => (idx >= from && idx < from + count ? { ...r, amount: Number(amount) } : r))));
  };
  const addRow = () => {
    setCustom(true);
    setRows((prev) => {
      const last = prev[prev.length - 1];
      let nextDate = new Date();
      if (last?.date) {
        nextDate = new Date(last.date);
        if (freq === "monthly") nextDate.setMonth(nextDate.getMonth() + 1);
        else nextDate.setDate(nextDate.getDate() + (freq === "daily" ? 1 : 7));
      }
      return recompute([...prev, { date: nextDate.toISOString().slice(0, 10), amount: last?.amount || meta?.regular_amount || 0 }]);
    });
  };
  const resetTable = () => { setCustom(false); };

  const planPayload = () => {
    const payload = { plan_type: planType || "temp_review", params, actor: "Ops" };
    if (custom) payload.rows = rows.map(({ date, amount, label }) => ({ date, amount: Number(amount || 0), label }));
    return payload;
  };

  const savePlan = async (thenEmail) => {
    if (!rows.length) { toast.error("Build a schedule first"); return; }
    try {
      setBusy(true);
      const r = await createPaymentPlan(caseRow.case_id, planPayload());
      setSavedPlan(r.plan);
      if (r.plan?.status === "pending_approval") {
        toast.success("Plan saved — awaiting manager approval before it can be emailed", { duration: 6000 });
        onDone?.();
        return;
      }
      toast.success("Payment plan saved to the case");
      if (thenEmail) setMode("email");
      else onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const exportPdf = async () => {
    if (!rows.length && !savedPlan) { toast.error("Build a schedule first"); return; }
    try {
      setPdfBusy(true);
      const blob = await exportPaymentPlanPdf(caseRow.case_id, planPayload());
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Payment Plan - ${caseRow.customer_name || caseRow.loan_application_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setPdfBusy(false);
    }
  };

  const sendEmail = async () => {
    try {
      setBusy(true);
      if (emailMode === "reply") {
        if (!replyMsg) { toast.error("Pick the email you're replying to"); setBusy(false); return; }
        const origFrom = replyMsg.from?.emailAddress?.address;
        await sendPaymentPlanEmail(caseRow.case_id, savedPlan.plan_id, {
          to: origFrom ? [origFrom] : [], intro: intro || undefined, actor: "Ops",
          reply_to_message_id: replyMsg.id,
        });
        toast.success("📧 Proposal sent as a reply in the thread");
      } else {
        const recipients = to.split(",").map((s) => s.trim()).filter(Boolean);
        if (!recipients.length) { toast.error("Add at least one recipient"); setBusy(false); return; }
        await sendPaymentPlanEmail(caseRow.case_id, savedPlan.plan_id, {
          to: recipients, subject, intro: intro || undefined, actor: "Ops",
        });
        toast.success(`📧 Payment plan emailed to ${recipients.join(", ")}`);
      }
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const totalCollected = useMemo(
    () => rows.reduce((s, r) => s + Number(r.amount || 0), 0), [rows]);

  const paramFields = planType && (
    <div className="space-y-2.5">
      {planType === "reduced_then_blend" && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Pay % of normal">
              <Input type="number" min={1} max={99} value={params.pay_pct ?? ""} placeholder="50"
                     onChange={(e) => setP("pay_pct", e.target.value)} data-testid="plan-param-pay-pct" />
            </Field>
            <Field label={`Reduced ${unit}`}>
              <Input type="number" min={1} value={params.reduced_periods ?? ""} placeholder="4"
                     onChange={(e) => setP("reduced_periods", e.target.value)} data-testid="plan-param-reduced-periods" />
            </Field>
          </div>
          {meta?.remaining_count === 0 && (
            <Field label="Loan matured — spread the remainder over how many payments?">
              <Input type="number" min={1} value={params.blend_periods ?? ""} placeholder="12"
                     onChange={(e) => setP("blend_periods", e.target.value)} data-testid="plan-param-blend-periods" />
            </Field>
          )}
        </>
      )}
      {planType === "holiday_then_lump" && (
        <Field label={`Payment holiday — ${unit} with no payments`}>
          <Input type="number" min={1} value={params.holiday_periods ?? ""} placeholder="2"
                 onChange={(e) => setP("holiday_periods", e.target.value)} data-testid="plan-param-holiday-periods" />
        </Field>
      )}
      {planType === "temp_review" && (
        <div className="grid grid-cols-2 gap-2">
          <Field label={`Temporary amount (${cur()})`}>
            <Input type="number" min={1} value={params.temp_amount ?? ""} placeholder="100"
                   onChange={(e) => setP("temp_amount", e.target.value)} data-testid="plan-param-temp-amount" />
          </Field>
          <Field label={`${unit.charAt(0).toUpperCase() + unit.slice(1)} at this amount`}>
            <Input type="number" min={1} value={params.temp_periods ?? ""} placeholder="6"
                   onChange={(e) => setP("temp_periods", e.target.value)} data-testid="plan-param-temp-periods" />
          </Field>
          <Field label="Review date (optional)">
            <Input type="date" value={params.review_date ?? ""}
                   onChange={(e) => setP("review_date", e.target.value)} data-testid="plan-param-review-date" />
          </Field>
        </div>
      )}
      {planType === "stepped" && (
        <PhaseEditor phases={params.phases || []} unit={unit} onChange={(ph) => setP("phases", ph)}
                     reviewDate={params.review_date ?? ""} onReview={(v) => setP("review_date", v)} />
      )}
      {planType === "settlement" && (
        <div className="grid grid-cols-2 gap-2">
          <Field label="Settlement date">
            <Input type="date" value={params.settlement_date ?? ""}
                   onChange={(e) => setP("settlement_date", e.target.value)} data-testid="plan-param-settlement-date" />
          </Field>
          <Field label="Amount (blank = full)">
            <Input type="number" min={1} value={params.settlement_amount ?? ""} placeholder={meta ? String(meta.outstanding_total) : ""}
                   onChange={(e) => setP("settlement_amount", e.target.value)} data-testid="plan-param-settlement-amount" />
          </Field>
        </div>
      )}
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="payment-plan-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HandCoins className="w-5 h-5 text-indigo-600" />
            Payment plan — {caseRow.customer_name || caseRow.loan_application_id}
            {mode === "email" && <Badge variant="outline" className="ml-1 text-[10px]">email proposal</Badge>}
            <WizardDebugButton className="ml-auto mr-6" />
          </DialogTitle>
        </DialogHeader>

        {metaErr ? (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3" data-testid="plan-meta-error">
            Couldn't load the LoanDisk schedule: {metaErr}
          </div>
        ) : !meta ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading live LoanDisk schedule…
          </div>
        ) : mode === "build" ? (
          <>
            {/* Loan snapshot */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center" data-testid="plan-loan-snapshot">
              <Stat label="Outstanding" value={`${cur()}${GBP(meta.outstanding_total)}`} strong />
              <Stat label="Arrears now" value={`${cur()}${GBP(meta.arrears_now)}`} tone={meta.arrears_now > 0 ? "text-rose-700" : ""} />
              <Stat label="Normal payment" value={`${cur()}${GBP(meta.regular_amount)}`} />
              <Stat label="Payments left" value={meta.remaining_count} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[340px,1fr] gap-4 mt-1">
              {/* ── Left: controls ── */}
              <div className="space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
                    Payment frequency <span className="normal-case font-normal">(per the agreement)</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {["daily", "weekly", "monthly"].map((f) => (
                      <button key={f} onClick={() => pickFreq(f)}
                              className={`border rounded-md px-2.5 py-1.5 text-xs font-semibold transition capitalize
                                ${freq === f ? "bg-slate-800 text-white border-slate-800" : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"}`}
                              data-testid={`plan-freq-${f}`}>
                        {f}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">Plan type</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {PLAN_OPTIONS.map((o) => {
                      const Icon = o.icon;
                      const active = planType === o.id;
                      return (
                        <button key={o.id}
                                onClick={() => { setPlanType(o.id); setParams(o.id === "stepped" ? { frequency: freq, phases: [newPhase(), { ...newPhase(), mode: "until_cleared" }] } : { frequency: freq }); setCustom(false); }}
                                className={`flex items-center gap-1.5 border rounded-md px-2.5 py-2 text-xs font-medium transition
                                  ${active ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"}`}
                                data-testid={`plan-type-${o.id}`}>
                          <Icon className="w-3.5 h-3.5 flex-shrink-0" /> {o.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {paramFields}

                {previewBusy && (
                  <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" /> Updating table…
                  </div>
                )}
                {planErr && (
                  <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2.5 py-1.5" data-testid="plan-error">{planErr}</div>
                )}
                {headline && !custom && (
                  <div className="text-xs font-medium bg-indigo-50 border border-indigo-200 rounded px-2.5 py-1.5" data-testid="plan-headline">{headline}</div>
                )}
                {custom && (
                  <div className="text-xs bg-slate-100 border rounded px-2.5 py-1.5 inline-flex items-center gap-1.5" data-testid="plan-custom-badge">
                    <Pencil className="w-3 h-3" /> Hand-edited schedule — saved exactly as shown
                  </div>
                )}
                {warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5" data-testid={`plan-warning-${i}`}>
                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {w}
                  </div>
                ))}
                {approvalInfo && !custom && (
                  <div className="text-[11px] text-orange-900 bg-orange-50 border border-orange-300 rounded px-2.5 py-1.5" data-testid="plan-approval-notice">
                    <div className="font-semibold flex items-center gap-1.5">
                      <AlertTriangle className="w-3 h-3" /> Exceeds your approval limits
                    </div>
                    <ul className="list-disc ml-4 mt-0.5">
                      {approvalInfo.breaches.map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                    <div className="mt-0.5">It will save as <strong>pending approval</strong> and alert a manager — the email can only go out once approved.</div>
                  </div>
                )}

                <div className="flex flex-col gap-1.5 pt-2 border-t">
                  {approvalInfo && !custom ? (
                    <Button onClick={() => savePlan(false)} disabled={busy || !rows.length}
                            className="bg-orange-600 hover:bg-orange-700" data-testid="plan-save-approval-btn">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                      Save for manager approval
                    </Button>
                  ) : (
                    <>
                      <Button onClick={() => savePlan(true)} disabled={busy || !rows.length} data-testid="plan-save-email-btn">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
                        Save &amp; email customer
                      </Button>
                      <Button variant="outline" onClick={() => savePlan(false)} disabled={busy || !rows.length} data-testid="plan-save-btn">
                        <Save className="w-4 h-4 mr-1.5" /> Save only
                      </Button>
                    </>
                  )}
                  <Button variant="ghost" size="sm" onClick={onClose} data-testid="plan-cancel-btn">Cancel</Button>
                </div>
              </div>

              {/* ── Right: live proposal table ── */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Proposal table</span>
                  {custom && <Badge variant="outline" className="text-[9px] bg-slate-100">edited</Badge>}
                  <div className="ml-auto flex gap-1">
                    {custom && (
                      <Button variant="ghost" size="sm" className="h-6 text-[11px] px-2" onClick={resetTable} data-testid="plan-reset-btn">
                        <RotateCcw className="w-3 h-3 mr-1" /> Regenerate
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="h-6 text-[11px] px-2" onClick={exportPdf}
                            disabled={pdfBusy || !rows.length} data-testid="plan-export-pdf-btn">
                      {pdfBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileDown className="w-3 h-3 mr-1" />} PDF
                    </Button>
                    <Button variant="outline" size="sm" className="h-6 text-[11px] px-2" onClick={addRow} disabled={!meta} data-testid="plan-add-row-btn">
                      <Plus className="w-3 h-3 mr-1" /> Add row
                    </Button>
                  </div>
                </div>

                {rows.length > 0 && <BulkSetBar rowCount={rows.length} onApply={bulkSet} />}
                {rows.length === 0 ? (
                  <div className="border border-dashed rounded-md h-56 flex items-center justify-center text-xs text-muted-foreground" data-testid="plan-table-empty">
                    {planType ? "Fill in the options — the table builds itself." : "Pick a plan type to start, or Add row to build a schedule by hand."}
                  </div>
                ) : (
                  <ProposalTable
                    name={caseRow.customer_name || caseRow.loan_application_id}
                    rows={rows}
                    opening={meta.outstanding_total}
                    editable
                    onEdit={editRow}
                    onRemove={removeRow}
                    reviewInfo={(() => {
                      if (planType !== "temp_review" && planType !== "stepped") return null;
                      const d = custom ? (params.review_date || reviewInfo?.date) : (reviewInfo?.date || params.review_date);
                      if (!d) return null;
                      return { date: d, remaining: custom ? rows[rows.length - 1]?.balance : reviewInfo?.remaining };
                    })()}
                  />
                )}

                {rows.length > 0 && (
                  <div className="text-[11px] text-muted-foreground" data-testid="plan-table-totals">
                    {rows.filter((r) => Number(r.amount) > 0).length} payments · collects {cur()}{GBP(totalCollected)} of {cur()}{GBP(meta.outstanding_total)}
                    {Math.abs(totalCollected - meta.outstanding_total) >= 0.01 && (
                      <span className={totalCollected < meta.outstanding_total ? " text-amber-700" : " text-rose-700"}>
                        {" "}({totalCollected < meta.outstanding_total ? "short" : "over"} by {cur()}{GBP(Math.abs(totalCollected - meta.outstanding_total))})
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          /* ── EMAIL MODE — takes over the whole wizard ── */
          <div className="space-y-3" data-testid="plan-email-panel">
            <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5">
              ✓ Plan saved — the proposal table goes into the email below your message.
            </div>

            {/* New vs Reply */}
            <div className="flex gap-1.5">
              <button onClick={() => setEmailMode("new")}
                      className={`flex items-center gap-1.5 border rounded-md px-3 py-1.5 text-xs font-semibold transition
                        ${emailMode === "new" ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"}`}
                      data-testid="plan-email-mode-new">
                <Mail className="w-3.5 h-3.5" /> New email
              </button>
              <button onClick={() => { setEmailMode("reply"); loadInbox(); }}
                      className={`flex items-center gap-1.5 border rounded-md px-3 py-1.5 text-xs font-semibold transition
                        ${emailMode === "reply" ? "bg-indigo-600 text-white border-indigo-600" : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"}`}
                      data-testid="plan-email-mode-reply">
                <Reply className="w-3.5 h-3.5" /> Reply to a thread
              </button>
            </div>

            {emailMode === "new" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="To (comma-separate multiple)">
                  <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@example.com" data-testid="plan-email-to" />
                  {contacts.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {contacts.map((c) => (
                        <button key={c.email} onClick={() => setTo(c.email)}
                                className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 hover:bg-slate-200 border"
                                data-testid={`plan-email-contact-${c.email}`}>
                          {c.label} · {c.email}
                        </button>
                      ))}
                    </div>
                  )}
                </Field>
                <Field label="Subject">
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="plan-email-subject" />
                </Field>
              </div>
            ) : (
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                  Pick the email to reply to — recipients &amp; subject stay in the thread
                </div>
                {inboxEmails === null || (inboxEmails.length === 0 && inboxEmails !== null && !replyMsg) ? (
                  inboxEmails === null ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground py-3">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading case inbox…
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground border border-dashed rounded p-3" data-testid="plan-email-inbox-empty">
                      No emails found for this customer — use "New email" instead.
                    </div>
                  )
                ) : (
                  <div className="border rounded-md divide-y max-h-64 overflow-auto" data-testid="plan-email-inbox-list">
                    {inboxEmails.map((e) => {
                      const selected = replyMsg?.id === e.id;
                      return (
                        <div key={e.id} className={selected ? "bg-indigo-50 border-l-2 border-indigo-500" : ""}>
                          <button onClick={() => setReplyMsg(selected ? null : e)}
                                  className="w-full text-left px-3 py-1.5 text-xs transition hover:bg-slate-50/80"
                                  data-testid={`plan-email-thread-${e.id}`}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium truncate">{e.from?.emailAddress?.name || e.from?.emailAddress?.address || "—"}</span>
                              <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                {e.receivedDateTime ? new Date(e.receivedDateTime).toLocaleDateString("en-GB") : ""}
                              </span>
                            </div>
                            <div className="truncate text-muted-foreground">{e.subject || "(no subject)"}</div>
                          </button>
                          {selected && (
                            <div className="px-3 pb-2 text-[11px] text-slate-600 whitespace-pre-wrap border-t border-indigo-100 pt-1.5"
                                 data-testid={`plan-email-thread-body-${e.id}`}>
                              {(e.bodyPreview || "").trim() || "(no preview available)"}
                              <div className="mt-1 text-[10px] text-indigo-600 font-medium">✓ Replying in this thread — click again to deselect</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <Field label="Message (leave blank for the standard wording — the table is appended automatically)">
              <Textarea rows={6} value={intro} onChange={(e) => setIntro(e.target.value)}
                        placeholder={"Dear {first_name},\n\nPlease review the below payment plan proposal…"}
                        data-testid="plan-email-intro" />
            </Field>

            {savedPlan && (
              <ProposalTable
                name={savedPlan.customer_name || caseRow.customer_name}
                rows={savedPlan.rows || []}
                opening={savedPlan.totals?.opening_balance}
                editable={false}
                reviewInfo={savedPlan.review_date ? { date: savedPlan.review_date, remaining: savedPlan.remaining_after_review } : null}
              />
            )}

            <div className="flex items-center justify-between gap-2 pt-2 border-t">
              <Button variant="ghost" size="sm" onClick={() => setMode("build")} data-testid="plan-email-back-btn">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to plan
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={exportPdf} disabled={pdfBusy} data-testid="plan-email-pdf-btn">
                  {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileDown className="w-4 h-4 mr-1.5" />} Export PDF
                </Button>
                <Button variant="ghost" onClick={() => onDone?.()} data-testid="plan-skip-email-btn">
                  Skip — plan already saved
                </Button>
                <Button onClick={sendEmail}
                        disabled={busy || (emailMode === "new" ? !to.trim() : !replyMsg)}
                        data-testid="plan-send-email-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
                  {emailMode === "reply" ? "Send reply with proposal" : "Send proposal"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PhaseEditor({ phases, unit, onChange, reviewDate, onReview }) {
  const upd = (i, patch) => onChange(phases.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const remove = (i) => onChange(phases.filter((_, idx) => idx !== i).map((p, idx, arr) => (idx < arr.length - 1 && p.mode !== "periods" ? { ...p, mode: "periods" } : p)));
  const add = () => onChange([...phases.map((p) => (p.mode !== "periods" ? { ...p, mode: "periods" } : p)), newPhase()]);
  return (
    <div className="space-y-1.5" data-testid="plan-phase-editor">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Phases <span className="normal-case font-normal">— amount per {unit.slice(0, -1)}, then how long</span></div>
      {phases.map((p, i) => {
        const last = i === phases.length - 1;
        return (
          <div key={i} className="flex items-center gap-1.5" data-testid={`plan-phase-${i}`}>
            <span className="text-[10px] text-muted-foreground w-4">{i + 1}.</span>
            <Input type="number" min={0} step="0.01" value={p.amount} placeholder="250" className="h-8 w-24 text-xs"
                   onChange={(e) => upd(i, { amount: e.target.value })} data-testid={`plan-phase-amount-${i}`} />
            <select value={p.mode || "periods"} onChange={(e) => upd(i, { mode: e.target.value })} disabled={!last}
                    className="h-8 text-xs border rounded px-1 bg-white disabled:bg-slate-50" data-testid={`plan-phase-mode-${i}`}>
              {STEP_MODES.filter((m) => last || m.id === "periods").map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            {(p.mode || "periods") === "periods" && (
              <>
                <Input type="number" min={1} value={p.periods} placeholder="6" className="h-8 w-16 text-xs"
                       onChange={(e) => upd(i, { periods: e.target.value })} data-testid={`plan-phase-periods-${i}`} />
                <span className="text-[10px] text-muted-foreground">{unit}</span>
              </>
            )}
            <button onClick={() => remove(i)} className="text-slate-300 hover:text-rose-600 ml-auto" title="Remove phase" disabled={phases.length === 1} data-testid={`plan-phase-remove-${i}`}><X className="w-3 h-3" /></button>
          </div>
        );
      })}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="h-7 text-[11px] px-2" onClick={add} data-testid="plan-phase-add-btn"><Plus className="w-3 h-3 mr-1" /> Add phase</Button>
        <span className="text-[10px] text-muted-foreground">Set amount 0 for a payment holiday. Only the last phase can run until cleared / blend to maturity.</span>
      </div>
      <Field label="Review date (optional)">
        <Input type="date" value={reviewDate} onChange={(e) => onReview(e.target.value)} data-testid="plan-param-review-date" />
      </Field>
    </div>
  );
}

function BulkSetBar({ rowCount, onApply }) {
  const [from, setFrom] = useState(1);
  const [count, setCount] = useState("");
  const [amount, setAmount] = useState("");
  const ok = Number(from) >= 1 && Number(from) <= rowCount && Number(count) >= 1 && amount !== "" && Number(amount) >= 0;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-[11px] bg-slate-50 border rounded px-2 py-1.5" data-testid="plan-bulk-bar">
      <Wand2 className="w-3 h-3 text-indigo-600" /><span className="font-semibold">Bulk set</span>
      <span>from row</span>
      <Input type="number" min={1} max={rowCount} value={from} onChange={(e) => setFrom(e.target.value)} className="h-6 w-14 text-[11px] px-1" data-testid="plan-bulk-from" />
      <span>next</span>
      <Input type="number" min={1} value={count} placeholder={String(rowCount)} onChange={(e) => setCount(e.target.value)} className="h-6 w-14 text-[11px] px-1" data-testid="plan-bulk-count" />
      <span>rows to {cur()}</span>
      <Input type="number" min={0} step="0.01" value={amount} placeholder="125" onChange={(e) => setAmount(e.target.value)} className="h-6 w-20 text-[11px] px-1" data-testid="plan-bulk-amount" />
      <Button size="sm" className="h-6 text-[11px] px-2" disabled={!ok}
              onClick={() => onApply({ from: Number(from) - 1, count: Number(count), amount })} data-testid="plan-bulk-apply-btn">Apply</Button>
      <button className="text-slate-500 hover:text-slate-800 underline" onClick={() => setCount(String(rowCount - Number(from) + 1))} data-testid="plan-bulk-rest-btn">rest of table</button>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function Stat({ label, value, strong, tone = "" }) {
  return (
    <div className="border rounded-md px-2 py-1.5 bg-white">
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-sm ${strong ? "font-bold" : "font-medium"} ${tone}`}>{value}</div>
    </div>
  );
}

// Bordered proposal table — matches the emailed/PDF version pixel-for-pixel.
function ProposalTable({ name, rows, opening, editable, onEdit, onRemove, reviewInfo }) {
  const td = "border border-slate-400 px-2 py-[3px] text-[12px] leading-tight";
  return (
    <div className="overflow-auto max-h-[52vh]" data-testid="plan-preview-table">
      <table className="border-collapse w-full max-w-md">
        <thead className="sticky top-0">
          <tr>
            <th colSpan={editable ? 4 : 3} className={`${td} bg-slate-200 text-center font-bold`}>{name}</th>
          </tr>
          <tr>
            <th className={`${td} bg-slate-100 font-semibold text-left`}>Date</th>
            <th className={`${td} bg-slate-100 font-semibold text-right`}>Amount</th>
            <th className={`${td} bg-slate-100 font-semibold text-right`}>Balance</th>
            {editable && <th className="w-6 border-0" />}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className={td} />
            <td className={td} />
            <td className={`${td} text-right font-medium`}>{GBP(opening)}</td>
            {editable && <td className="border-0" />}
          </tr>
          {rows.map((r, i) => (
            <tr key={i} data-testid={`plan-table-row-${i}`}>
              <td className={`${td} whitespace-nowrap`}>
                {editable ? (
                  <input type="date" value={r.date}
                         onChange={(e) => onEdit(i, { date: e.target.value })}
                         className="bg-transparent text-[12px] w-[110px] focus:outline-none focus:bg-blue-50"
                         data-testid={`plan-row-date-${i}`} />
                ) : UK(r.date)}
                {r.label && <span className="text-slate-400 text-[10px]"> · {r.label}</span>}
              </td>
              <td className={`${td} text-right`}>
                {editable ? (
                  <input type="number" step="0.01" value={r.amount}
                         onChange={(e) => onEdit(i, { amount: e.target.value })}
                         className="bg-transparent text-[12px] text-right w-[84px] focus:outline-none focus:bg-blue-50"
                         data-testid={`plan-row-amount-${i}`} />
                ) : GBP(r.amount)}
              </td>
              <td className={`${td} text-right`}>{GBP(r.balance)}</td>
              {editable && (
                <td className="border-0 pl-1">
                  <button onClick={() => onRemove(i)} className="text-slate-300 hover:text-rose-600" title="Remove row"
                          data-testid={`plan-row-remove-${i}`}>
                    <X className="w-3 h-3" />
                  </button>
                </td>
              )}
            </tr>
          ))}
          {reviewInfo?.date && (
            <tr>
              <td className={`${td} font-bold`}>{UK(reviewInfo.date)}</td>
              <td className={`${td} text-right font-bold`}>Review</td>
              <td className={`${td} text-right font-bold`}>{GBP(reviewInfo.remaining)}</td>
              {editable && <td className="border-0" />}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export const PaymentPlanWizard = desktopOnly(PaymentPlanWizardInner, "The payment plan wizard");
